import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, ArrowLeft, RefreshCw, Database, HardDrive, Trash2, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/admin/KpiCard";
import { SEO } from "@/components/SEO";
import { formatDistanceToNow } from "date-fns";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

// Supabase Storage cost (approx, USD): $0.021 per GB-month for storage,
// $0.09 per GB egress. We surface the storage component as a monthly estimate.
const STORAGE_PRICE_USD_PER_GB_MONTH = 0.021;

interface BucketRow {
  bucket: string;
  objects: number;
  bytes: number;
  oldest: string | null;
  newest: string | null;
}
interface DailyPoint {
  date: string;
  bucket: string;
  added_objects: number;
  added_bytes: number;
}
interface LargestObj {
  bucket: string;
  name: string;
  bytes: number;
  created_at: string;
}
interface CloudStats {
  ok: true;
  checkedAt: string;
  buckets: BucketRow[];
  daily: DailyPoint[];
  largest: LargestObj[];
  tryonStats: { total: number; favorited: number; pruneCandidates: number };
}

const fmtBytes = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
};

const fmtUSD = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 4 });

const BUCKET_COLOR: Record<string, string> = {
  "tryon-results": "hsl(var(--primary))",
  "user-photos": "hsl(var(--accent))",
  "product-images": "hsl(var(--muted-foreground))",
};

export default function AdminCloud() {
  const isAdmin = useIsAdmin();
  const [data, setData] = useState<CloudStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (isAdmin !== true) return;
    let active = true;
    setLoading(true);
    setError(null);
    supabase.functions
      .invoke("admin-storage-stats", { body: {} })
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          setError(error.message ?? "Failed to load stats");
          return;
        }
        setData(data as CloudStats);
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isAdmin, tick]);

  const totals = useMemo(() => {
    const buckets = data?.buckets ?? [];
    const objects = buckets.reduce((s, b) => s + b.objects, 0);
    const bytes = buckets.reduce((s, b) => s + b.bytes, 0);
    const gb = bytes / (1024 ** 3);
    const monthlyCost = gb * STORAGE_PRICE_USD_PER_GB_MONTH;
    return { objects, bytes, gb, monthlyCost };
  }, [data]);

  const dailySeries = useMemo(() => {
    if (!data) return [] as Array<Record<string, number | string>>;
    // Build a continuous 30-day axis with one column per bucket.
    const buckets = Array.from(new Set(data.buckets.map((b) => b.bucket)));
    const map = new Map<string, Record<string, number | string>>();
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const key = d.toISOString().slice(0, 10);
      const row: Record<string, number | string> = { date: key.slice(5) };
      for (const b of buckets) row[b] = 0;
      map.set(key, row);
    }
    for (const p of data.daily) {
      const row = map.get(p.date);
      if (!row) continue;
      row[p.bucket] = ((row[p.bucket] as number) ?? 0) + p.added_bytes / (1024 * 1024); // MB
    }
    return Array.from(map.values());
  }, [data]);

  const bucketKeys = useMemo(
    () => (data ? Array.from(new Set(data.buckets.map((b) => b.bucket))) : []),
    [data],
  );

  if (isAdmin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/app" replace />;

  return (
    <>
      <SEO title="Admin Cloud · FitAura AI" description="Internal cloud storage dashboard." noindex />
      <div className="relative min-h-screen overflow-hidden bg-background">
        <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[140px]" />
        </div>

        <header className="sticky top-0 z-10 border-b border-white/10 bg-background/80 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3">
            <Button asChild size="icon" variant="ghost">
              <Link to="/app/profile" aria-label="Back">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div className="min-w-0">
              <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
                — ADMIN
              </span>
              <h1 className="font-display truncate text-base font-bold uppercase tracking-[0.18em]">
                Cloud Dashboard
              </h1>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button asChild size="sm" variant="outline">
                <Link to="/admin/costs">AI Costs →</Link>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setTick((n) => n + 1)}
                disabled={loading}
              >
                <RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
              </Button>
            </div>
          </div>
          {data?.checkedAt && (
            <div className="mx-auto max-w-7xl px-4 pb-2 text-[11px] text-muted-foreground">
              Updated {formatDistanceToNow(new Date(data.checkedAt), { addSuffix: true })}
            </div>
          )}
        </header>

        <main className="mx-auto max-w-7xl space-y-6 p-4">
          {error ? (
            <Card className="border-destructive">
              <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
            </Card>
          ) : null}

          {loading && !data ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : data ? (
            <>
              {/* KPI strip */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
                <KpiCard
                  label="Total storage"
                  value={fmtBytes(totals.bytes)}
                  sub={`${totals.gb.toFixed(2)} GB`}
                  delta={null}
                  showCompare={false}
                />
                <KpiCard
                  label="Total objects"
                  value={totals.objects.toLocaleString()}
                  delta={null}
                  showCompare={false}
                />
                <KpiCard
                  label="Est. monthly cost"
                  value={fmtUSD(totals.monthlyCost)}
                  sub={`@ $${STORAGE_PRICE_USD_PER_GB_MONTH}/GB-mo`}
                  delta={null}
                  showCompare={false}
                />
                <KpiCard
                  label="Total try-ons (rows)"
                  value={(data.tryonStats.total ?? 0).toLocaleString()}
                  sub={`${data.tryonStats.favorited} favorited (local-only)`}
                  delta={null}
                  showCompare={false}
                />
                <KpiCard
                  label="Images live in cloud"
                  value={(data.tryonStats.pruneCandidates ?? 0).toLocaleString()}
                  sub="Auto-deleted 24h after generation"
                  delta={null}
                  showCompare={false}
                />
              </div>

              {/* Daily growth chart (MB added per day) */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Storage growth (last 30 days, MB added/day)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dailySeries}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{
                            background: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            fontSize: 12,
                          }}
                          formatter={(v: number) => `${v.toFixed(2)} MB`}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {bucketKeys.map((b) => (
                          <Area
                            key={b}
                            type="monotone"
                            dataKey={b}
                            stackId="1"
                            stroke={BUCKET_COLOR[b] ?? "hsl(var(--primary))"}
                            fill={BUCKET_COLOR[b] ?? "hsl(var(--primary))"}
                            fillOpacity={0.35}
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Bucket breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Buckets</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-border text-left text-xs text-muted-foreground">
                        <tr>
                          <th className="py-2 pr-3">Bucket</th>
                          <th className="py-2 pr-3 text-right">Objects</th>
                          <th className="py-2 pr-3 text-right">Total size</th>
                          <th className="py-2 pr-3 text-right">Avg / object</th>
                          <th className="py-2 pr-3 text-right">Est. $/mo</th>
                          <th className="py-2 pr-3">Newest</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.buckets.map((b) => {
                          const avg = b.objects ? b.bytes / b.objects : 0;
                          const cost = (b.bytes / (1024 ** 3)) * STORAGE_PRICE_USD_PER_GB_MONTH;
                          return (
                            <tr key={b.bucket} className="border-b border-border/50">
                              <td className="py-2 pr-3 font-medium">
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    className="inline-block h-2.5 w-2.5 rounded-full"
                                    style={{ background: BUCKET_COLOR[b.bucket] ?? "hsl(var(--primary))" }}
                                  />
                                  {b.bucket}
                                </span>
                              </td>
                              <td className="py-2 pr-3 text-right tabular-nums">
                                {b.objects.toLocaleString()}
                              </td>
                              <td className="py-2 pr-3 text-right tabular-nums">{fmtBytes(b.bytes)}</td>
                              <td className="py-2 pr-3 text-right tabular-nums">{fmtBytes(avg)}</td>
                              <td className="py-2 pr-3 text-right tabular-nums">{fmtUSD(cost)}</td>
                              <td className="py-2 pr-3 text-xs text-muted-foreground">
                                {b.newest
                                  ? formatDistanceToNow(new Date(b.newest), { addSuffix: true })
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Try-on storage hygiene */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Try-on storage hygiene</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-md border border-border p-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Database className="h-3.5 w-3.5" /> Total try-ons
                    </div>
                    <div className="mt-1 text-lg font-semibold tabular-nums">
                      {data.tryonStats.total.toLocaleString()}
                    </div>
                  </div>
                  <div className="rounded-md border border-border p-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Star className="h-3.5 w-3.5" /> Favorited (local-only)
                    </div>
                    <div className="mt-1 text-lg font-semibold tabular-nums">
                      {data.tryonStats.favorited.toLocaleString()}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Saved to user's device — cloud image still expires in 24h
                    </div>
                  </div>
                  <div className="rounded-md border border-border p-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Trash2 className="h-3.5 w-3.5" /> Images still in cloud
                    </div>
                    <div className="mt-1 text-lg font-semibold tabular-nums">
                      {data.tryonStats.pruneCandidates.toLocaleString()}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Auto-swept hourly once past 24h TTL
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Largest objects */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top 10 largest objects</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-border text-left text-xs text-muted-foreground">
                        <tr>
                          <th className="py-2 pr-3">#</th>
                          <th className="py-2 pr-3">Bucket</th>
                          <th className="py-2 pr-3">Path</th>
                          <th className="py-2 pr-3 text-right">Size</th>
                          <th className="py-2 pr-3">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.largest.map((o, i) => (
                          <tr key={`${o.bucket}-${o.name}`} className="border-b border-border/50">
                            <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                            <td className="py-2 pr-3">
                              <span className="inline-flex items-center gap-1.5">
                                <HardDrive className="h-3 w-3 text-muted-foreground" />
                                {o.bucket}
                              </span>
                            </td>
                            <td className="max-w-[420px] truncate py-2 pr-3 font-mono text-xs">
                              {o.name}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums">{fmtBytes(o.bytes)}</td>
                            <td className="py-2 pr-3 text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(o.created_at), { addSuffix: true })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : null}
        </main>
      </div>
    </>
  );
}
